import { useEffect, useState } from "react";
import { KeywordButtonType } from "@components/Button/utils/types";
import useMyBtStore from "@features/mybt/store/useMyBTStore";
import { useHandleUserAction } from "@features/mySph/hooks/useHandleUserAction";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { UserTypeEnum } from "@features/mySph/utils/types";
import {
  EMPTY_NOTIFICATION,
  KEYWORD_ADDED_SUCCESSFUL_NOTIFICATION,
  KEYWORD_REACH_LIMIT_NOTIFICATION,
  KEYWORD_REMOVED_SUCCESSFUL_NOTIFICATION,
  KEYWORD_UPDATED_UNSUCCESSFUL_NOTIFICATION,
} from "@features/notifications/NotificationToasts/utils/constants";
import { NotificationType } from "@features/notifications/NotificationToasts/utils/types";
import useNotificationStore from "@features/notifications/store/useNotificationStore";
import { FeedTag } from "@routes/Keyword/utils/types";
import { MyBtUpdateResponse } from "@routes/MyBT/utils/types";
import { tryCatch } from "@typings/TryCatch";
import { MYBT_REG_LIMIT, MYBT_SUB_LIMIT } from "@util/constant";
import { gaEventTracker } from "@util/helpers";
import axios, { HttpStatusCode } from "axios";

type UseHandleMyBtKeywordsProps = {
  tag: FeedTag;
  keywordButtonType: KeywordButtonType;
};

export const useHandleMyBtKeywords = ({
  tag,
  keywordButtonType,
}: UseHandleMyBtKeywordsProps) => {
  const { userType, visitorId } = useUserInfo();
  const { handleUserLogin } = useHandleUserAction();
  const [
    addTag,
    minusTag,
    followedTagsStore,
    setIsLimitModalShown,
    isLimitModalShown,
  ] = useMyBtStore((store) => [
    store.followTag,
    store.unfollowTag,
    store.tags,
    store.setIsLimitModalShown,
    store.isLimitModalShown,
    store.setIsLoading,
    store.setMyBtMessage,
    store.setIsMyBtBannerShown,
  ]);

  const [
    setIsNotificationLoading,
    setIsNotificationDisplayed,
    setNotification,
  ] = useNotificationStore((store) => [
    store.setIsNotificationLoading,
    store.setIsNotificationDisplayed,
    store.setNotification,
  ]);

  const [isFollowed, setIsFollowed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isWithError, setIsWithError] = useState(false);
  const [isModalShown, setIsModalShown] = useState(false);
  const [errorMessages, setErrorMessages] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [method, setMethod] = useState("add");

  const handleNotification = (
    isLoading: boolean,
    isDisplayed: boolean,
    notification: NotificationType
  ) => {
    setIsNotificationLoading(isLoading);
    setIsNotificationDisplayed(isDisplayed);
    setNotification(notification);
  };

  const handleLimitModalClose = (isLimitModalShown: boolean) => {
    setIsLimitModalShown(isLimitModalShown);
  };

  const handleFollowKeywordLimit = () => {
    setIsLoading(false);

    if (keywordButtonType === KeywordButtonType.version3) {
      handleNotification(false, true, KEYWORD_REACH_LIMIT_NOTIFICATION);

      return;
    }

    setIsLimitModalShown(true);
  };

  const handleKeywordError = () => {
    setErrorMessages(true);
    setIsModalShown(true);

    if (keywordButtonType === KeywordButtonType.version3) {
      setIsNotificationLoading(false);
      setNotification(KEYWORD_UPDATED_UNSUCCESSFUL_NOTIFICATION);
    }
  };

  const handleFollow = async () => {
    if (userType === UserTypeEnum.ANONYMOUS) {
      handleUserLogin();

      return;
    }

    if (!visitorId) return;

    setMethod("add");
    setIsLoading(true);
    setIsWithError(false);

    const limit =
      userType === UserTypeEnum.SUBSCRIBER ? MYBT_SUB_LIMIT : MYBT_REG_LIMIT;
    const isHitLimit = followedTagsStore.length >= limit;

    if (isHitLimit) {
      handleFollowKeywordLimit();

      return;
    }

    if (keywordButtonType === KeywordButtonType.version3) {
      handleNotification(false, true, EMPTY_NOTIFICATION);
    }

    const response = await handleAddOrDeleteMyBtFollowedKeywords(
      userType,
      visitorId,
      [tag],
      "add"
    );

    if (
      !response.success ||
      !response.data ||
      (response.data.errors && response.data.errors.length > 0)
    ) {
      handleKeywordError();

      return;
    }

    addTag(tag);
    setIsModalShown(true);

    if (keywordButtonType === KeywordButtonType.version3) {
      handleNotification(false, true, KEYWORD_ADDED_SUCCESSFUL_NOTIFICATION);
    }

    gaEventTracker("mybt", "add", tag.name);
    setIsLoading(false);
  };

  const handleUnfollow = async () => {
    if (userType === UserTypeEnum.ANONYMOUS) {
      handleUserLogin();

      return;
    }

    if (!visitorId) return;

    setMethod("delete");
    setIsLoading(true);
    setIsWithError(false);

    if (keywordButtonType === KeywordButtonType.version3) {
      handleNotification(false, true, EMPTY_NOTIFICATION);
    }

    const response = await handleAddOrDeleteMyBtFollowedKeywords(
      userType,
      visitorId,
      [tag],
      "delete"
    );

    if (
      !response.success ||
      !response.data ||
      (response.data.errors && response.data.errors.length > 0)
    ) {
      handleKeywordError();

      return;
    }

    minusTag(tag);
    setIsModalShown(true);

    if (keywordButtonType === KeywordButtonType.version3) {
      handleNotification(false, true, KEYWORD_REMOVED_SUCCESSFUL_NOTIFICATION);
    }

    gaEventTracker("mybt", "remove", tag.name);
    setIsLoading(false);
  };

  const handleCloseModal = () => {
    setIsModalShown(false);
    setErrorMessages(false);
  };

  useEffect(() => {
    if (!followedTagsStore) return;
    if (!tag) return;

    const isFollowed = followedTagsStore.some(
      (followedTag) => followedTag.name === tag.name
    );
    setIsFollowed(isFollowed);
  }, [followedTagsStore, tag]);

  return {
    isFollowed,
    handleFollow,
    handleUnfollow,
    isLoading,
    isWithError,
    isModalShown,
    errorMessages,
    isLimitModalShown,
    handleLimitModalClose,
    method,
    isTooltipOpen,
    setIsTooltipOpen,
    handleCloseModal,
  };
};

const handleAddOrDeleteMyBtFollowedKeywords = async (
  userType: UserTypeEnum,
  visitorId: string,
  tags: FeedTag[],
  method: "add" | "delete"
) => {
  const url =
    method === "add"
      ? "/_plat/api/v1/mybt/update"
      : "/_plat/api/v1/mybt/delete";

  const request = axios.post<MyBtUpdateResponse[]>(
    url,
    { tags: tags },
    { headers: { usertype: userType, aovisitorid: visitorId } }
  );

  const { data, error } = await tryCatch(request);

  if (error) {
    return {
      success: false,
      error: { code: HttpStatusCode.BadRequest, message: error.message },
    };
  }

  return {
    success: true,
    data: data.data[0],
  };
};
